import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { Calendar, MapPin, Users, Clock, DollarSign, List, Plane, MoreHorizontal, Plus, Edit, Trash2 } from 'lucide-react';
import { useTrips } from '@/hooks/use-trips';

interface Trip {
  id: string;
  date: Date;
  location: string;
  capacity: number;
  registered: number;
  status: 'open' | 'full' | 'closed';
  duration: string;
  activities: string[];
  price: number;
  included: string[];
}

export function OrganizerDashboard() {
  const { trips, loading, addTrip, updateTrip, deleteTrip } = useTrips();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTrip, setEditTrip] = useState<Trip | null>(null);
  const [form, setForm] = useState<Partial<Trip>>({});

  // Add Trip
  const handleAddTrip = async () => {
    if (!form.date || !form.location || !form.capacity || !form.duration || !form.price) return;
    await addTrip({
      date: new Date(form.date),
      location: form.location,
      capacity: Number(form.capacity),
      registered: 0,
      status: 'open',
      duration: form.duration,
      activities: form.activities || [],
      price: Number(form.price),
      included: form.included || []
    });
    setIsAddOpen(false);
    setForm({});
  };

  // Edit Trip
  const handleEditTrip = async () => {
    if (!editTrip || !form.date || !form.location || !form.capacity || !form.duration || !form.price) return;
    await updateTrip(editTrip.id, {
      date: new Date(form.date),
      location: form.location,
      capacity: Number(form.capacity),
      duration: form.duration,
      activities: form.activities || [],
      price: Number(form.price),
      included: form.included || []
    });
    setIsEditOpen(false);
    setEditTrip(null);
    setForm({});
  };

  // Delete Trip
  const handleDeleteTrip = async (id: string) => {
    await deleteTrip(id);
  };

  // Open Edit Modal
  const openEditModal = (trip: Trip) => {
    setEditTrip(trip);
    setForm({ ...trip, date: trip.date.toISOString().slice(0, 10) });
    setIsEditOpen(true);
  };

  return (
    <Card>
      <CardHeader className="border-b bg-muted/50 flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Plane className="h-5 w-5 text-primary" />
          Organizer - Manage Trips
        </CardTitle>
        <Button onClick={() => setIsAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Trip
        </Button>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="text-center py-8">Loading trips...</div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.map((trip) => (
                  <TableRow key={trip.id}>
                    <TableCell>{format(trip.date, 'PPP')}</TableCell>
                    <TableCell>{trip.location}</TableCell>
                    <TableCell>{trip.duration}</TableCell>
                    <TableCell>{trip.status}</TableCell>
                    <TableCell>{trip.registered}/{trip.capacity}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(trip)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteTrip(trip.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Add Trip Modal */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Trip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input type="date" placeholder="Date" value={form.date || ''} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            <Input placeholder="Location" value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
            <Input placeholder="Duration" value={form.duration || ''} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} />
            <Input placeholder="Capacity" type="number" value={form.capacity || ''} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} />
            <Input placeholder="Price" type="number" value={form.price || ''} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            <Input placeholder="Activities (comma separated)" value={form.activities || ''} onChange={e => setForm(f => ({ ...f, activities: e.target.value.split(',').map(a => a.trim()) }))} />
            <Input placeholder="Included (comma separated)" value={form.included || ''} onChange={e => setForm(f => ({ ...f, included: e.target.value.split(',').map(a => a.trim()) }))} />
            <Button onClick={handleAddTrip}>Add Trip</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Trip Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Trip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input type="date" placeholder="Date" value={form.date || ''} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            <Input placeholder="Location" value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
            <Input placeholder="Duration" value={form.duration || ''} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} />
            <Input placeholder="Capacity" type="number" value={form.capacity || ''} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} />
            <Input placeholder="Price" type="number" value={form.price || ''} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            <Input placeholder="Activities (comma separated)" value={form.activities || ''} onChange={e => setForm(f => ({ ...f, activities: e.target.value.split(',').map(a => a.trim()) }))} />
            <Input placeholder="Included (comma separated)" value={form.included || ''} onChange={e => setForm(f => ({ ...f, included: e.target.value.split(',').map(a => a.trim()) }))} />
            <Button onClick={handleEditTrip}>Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
} 